import * as Api from "@api";
import { Form } from "@components";
import { SUPPORT_EMAIL_LINK } from "@constants";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Typography,
} from "@material-ui/core";
import { CheckCircleOutline as CheckCircleOutlineIcon } from "@material-ui/icons";
import { Rating } from "@material-ui/lab";
import React from "react";
import { useHistory } from "react-router-dom";
import { Actions, Robot } from "../../../../../components";
import { init, reducer } from "./state";

type Props = {
  numbers: string[];
  open: boolean;
};

export const SuccessModal: React.FC<Props> = (props) => {
  const history = useHistory();
  const [state, dispatch] = React.useReducer(reducer, {}, init);

  // Handlers

  function handleRatingSelectorValueChange(
    name: "rating",
    rating: number | null
  ): void {
    dispatch({
      type: "RATING:UPDATE",
      rating,
    });
  }

  function handleCommentsTextareaValueChange(
    name: "comments",
    comments: string
  ): void {
    dispatch({
      type: "COMMENTS:UPDATE",
      comments,
    });
  }

  function handleDoneButtonClick(): void {
    dispatch({
      type: "IN_PROGRESS:TOGGLE",
      value: true,
    });
    Api.putRating({
      comments: state.comments,
      referenceNumbers: props.numbers,
      stars: state.rating, // stars can be null
    }).then(({ success }) => {
      dispatch({
        type: "IN_PROGRESS:TOGGLE",
        value: false,
      });
      if (success) {
        history.push("/dashboard/my-request", success);
      }
    });
  }

  // Helpers

  function getReferenceNumbersSectionLabel(): string {
    const count = props.numbers.length;

    return `Your reference number${count === 1 ? `` : `s`} ${
      count === 1 ? `is` : `are`
    }:`;
  }

  return props.numbers.length ? (
    <Dialog fullWidth maxWidth="lg" open={props.open} scroll="paper">
      <DialogTitle disableTypography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3} />
          <Grid item xs={9}>
            <Box display="flex" alignItems="center">
              <Box color="success.main" display="flex" pr={1}>
                <CheckCircleOutlineIcon />
              </Box>
              <Typography variant="subtitle2">
                Your request has been successfully submitted and now queued for
                review and approval.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Robot message="Good news! You can now check the status of your request via chat.google.com Message @GearsBot to start a conversation. Thank you for using GEARS!" />
          </Grid>
          <Grid item xs={9}>
            <Box color="success.main">
              <Typography variant="h6" color="textPrimary" align="center">
                {getReferenceNumbersSectionLabel()}
              </Typography>
              <Typography variant="body1" align="center">
                {props.numbers.join(", ")}
              </Typography>
            </Box>
            <Box pt={2} pb={4}>
              <Typography variant="body2" align="center">
                Once this GEARS ticket is approved a Service Manager ticket will
                be automatically created to initiate fulfilment of your request.
              </Typography>
            </Box>
            <Divider />
            <Box py={4}>
              <Typography variant="body2" align="center">
                You have questions or clarifications?
              </Typography>
              <Typography variant="body2" align="center">
                <Link href="/feedback">Click Here to Send your feedback</Link>
              </Typography>
              <Box pt={2}>
                <Typography variant="body2" align="center">
                  Or contact the following:
                </Typography>
                <Typography variant="body2" align="center">
                  <Link
                    href={SUPPORT_EMAIL_LINK}
                    target="_blank"
                    color="primary"
                    rel="noopener noreferrer"
                  >
                    gears_help@globalpay.com
                  </Link>
                </Typography>
              </Box>
              <Box pt={2}>
                <Typography variant="body2" align="center">
                  Service Desk at <strong>1-833-476-3375</strong> extension{" "}
                  <strong>98555</strong>
                </Typography>
              </Box>
            </Box>
            <Grid
              container
              direction="column"
              justify="center"
              alignItems="center"
            >
              <Grid item>
                <Rating
                  name="rating"
                  onChange={(event, value) =>
                    handleRatingSelectorValueChange("rating", value)
                  }
                  value={state.rating}
                />
              </Grid>
              <Grid item>
                <Typography variant="caption" display="block" align="center">
                  Rate your experience
                </Typography>
              </Grid>
            </Grid>
            <Box pt={4} pb={2} alignItems="center">
              <Form>
                <Form.Textarea
                  label="Add comment(s) here"
                  maxLength={400}
                  name="comments"
                  onChange={handleCommentsTextareaValueChange}
                  rows={3}
                  rowsMax={3}
                  size={12}
                  value={state.comments}
                />
              </Form>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Actions>
              <Actions.Action
                onClick={handleDoneButtonClick}
                pending={state.in_progress}
              >
                {state.in_progress ? "Submitting..." : "Done"}
              </Actions.Action>
            </Actions>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  ) : null;
};
